import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { DatacloudColumn } from '../../models/appcloud/datacloud-column.model';

@Injectable({
    providedIn: 'root',
})
export class DatacloudColumnService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private DATACLOUD_TABLES_URL: string = environment.publicApiHost + '/appcloud/datacloud-tables';

    getById(tableId: number, colId: number, additionalHeaders: any = {}): Observable<DatacloudColumn> {
        this.#spinnerService.addProcess('DatacloudColumnService.getById.' + colId, 'Loading your column...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .get<DatacloudColumn>(`${this.DATACLOUD_TABLES_URL}/${tableId}/datacloud-columns/${colId}`, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving column data from Relationship One. Please try again later.'
                    );
                    return of<DatacloudColumn>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudColumnService.getById.' + colId))
            );
    }

    findAll(tableId: number, additionalHeaders: any = {}): Observable<DatacloudColumn[]> {
        this.#spinnerService.addProcess('DatacloudColumnService.findAll.' + tableId, 'Loading your columns...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .get<DatacloudColumn[]>(`${this.DATACLOUD_TABLES_URL}/${tableId}/datacloud-columns`, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving column data from Relationship One. Please try again later.'
                    );
                    return of<DatacloudColumn[]>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudColumnService.findAll.' + tableId))
            );
    }

    create(tableId: number, dbColumn: DatacloudColumn, additionalHeaders: any = {}): Observable<DatacloudColumn> {
        this.#spinnerService.addProcess('DatacloudColumnService.create', 'Creating your datacloud column...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .post<DatacloudColumn>(`${this.DATACLOUD_TABLES_URL}/${tableId}/datacloud-columns`, dbColumn, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while saving your column. Please try again later.'
                    );
                    return of<DatacloudColumn>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess('DatacloudColumnService.create');
                })
            );
    }

    update(tableId: number, colId: number, dcColumn: DatacloudColumn, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess(
            'DatacloudColumnService.update.' + colId,
            'Updaing your column configuration...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .put<DatacloudColumn>(
                `${this.DATACLOUD_TABLES_URL}/${tableId}/datacloud-columns/${colId}`,
                dcColumn,
                httpOptions
            )
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your column. Please try again later.'
                    );
                    return of<DatacloudColumn>();
                }),
                finalize(() => this.#spinnerService.completeProcess('DatacloudColumnService.update.' + colId))
            );
    }

    delete(id: number, tableId: number, additionalHeaders: any = {}): Observable<DatacloudColumn[]> {
        const spinnerProcess = 'DatacloudColumnService.delete';
        this.#spinnerService.addProcess(spinnerProcess, 'Deleting your column...');

        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const columnDropURL = this.DATACLOUD_TABLES_URL + `/${tableId}/datacloud-columns/${id}`;

        return this.#http.delete<DatacloudColumn[]>(columnDropURL, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to delete the column. Please try again later.'
                );
                return of<DatacloudColumn[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerProcess))
        );
    }
}
